document.addEventListener('DOMContentLoaded', function() {
  // === CONFIGURATION ===
  const TARGET_SELECTOR = 'text-component[ref="cartTotal"], [data-cart-subtotal], .cart__total-value';
  const DEBUG = false; // Temporarily enabled for troubleshooting

  // === GLOBAL STATE (WITH PROPER CLEANUP) ===
  let lastCartTotal = -1;
  let valueObserver = null;
  let bootstrapObserver = null;
  let currentLanguage = 'it';
  let currentCurrency = 'EUR';
  let currentThreshold = 79;
  let isInitialized = false;
  let eventListeners = [];
  let updateTimeoutId = null;
  let observerTriggerCount = 0;
  let isUpdating = false; // Prevents observer loops

  // === UTILITIES ===
  function log(...args) { 
    if (DEBUG) console.log('[shipping-progress]', ...args); 
  }

  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  function throttle(func, limit) {
    let inThrottle;
    return function() {
      const args = arguments;
      const context = this;
      if (!inThrottle) {
        func.apply(context, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    }
  }

  // === CLEANUP FUNCTIONS ===
  function disconnectObservers() {
    if (valueObserver) {
      log('Disconnecting value observer');
      valueObserver.disconnect();
      valueObserver = null;
    }
    if (bootstrapObserver) {
      log('Disconnecting bootstrap observer');
      bootstrapObserver.disconnect();
      bootstrapObserver = null;
    }
  }

  function clearTimeouts() {
    if (updateTimeoutId) {
      clearTimeout(updateTimeoutId);
      updateTimeoutId = null;
    }
  }

  function removeEventListeners() {
    eventListeners.forEach(({element, event, handler, options}) => {
      try {
        element.removeEventListener(event, handler, options);
      } catch (e) {
        log('Error removing event listener:', e);
      }
    });
    eventListeners = [];
  }

  function cleanup() {
    log('Performing cleanup...');
    disconnectObservers();
    clearTimeouts();
    removeEventListeners();
    isUpdating = false;
    observerTriggerCount = 0;
    lastCartTotal = -1;
  }

  function addEventListenerSafe(element, event, handler, options = false) {
    element.addEventListener(event, handler, options);
    eventListeners.push({element, event, handler, options});
  }

  // === STEP 1: LANGUAGE AND CURRENCY DETECTION ===
  function detectLanguageAndCurrency() {
    // Detect language from URL
    const path = window.location.pathname;
    let language = 'it'; // default

    if (path.startsWith('/en')) language = 'en';
    else if (path.startsWith('/de')) language = 'de';
    else if (path.startsWith('/fr')) language = 'fr';
    else if (path.startsWith('/es')) language = 'es';

    // Detect currency from page content
    let currency = 'EUR'; // default
    const currencySelectors = [
      '.money', 
      '.price', 
      '[data-currency]', 
      '.cart__total-value',
      'text-component[ref="cartTotal"]',
      '[data-cart-subtotal]'
    ];

    for (let selector of currencySelectors) {
      const elements = document.querySelectorAll(selector);
      for (let el of elements) {
        const text = el.textContent || el.getAttribute('value') || '';
        if (text.includes('$')) {
          currency = 'USD';
          break;
        } else if (text.includes('£')) {
          currency = 'GBP';
          break;
        } else if (text.includes('€')) {
          currency = 'EUR';
          break;
        }
      }
      if (currency !== 'EUR') break;
    }

    log('Detected language:', language, 'currency:', currency);
    return { language, currency };
  }

  // === STEP 2: TRANSLATIONS ===
  const translations = {
    it: {
      remaining: 'Ti mancano ancora <span class="amount-to-free-shipping">{amount}</span> per la spedizione gratuita!',
      unlocked: '✅ <strong>Spedizione gratuita sbloccata!</strong>',
      startLabel: '0',
      endLabel: '{threshold}'
    },
    en: {
      remaining: 'You need <span class="amount-to-free-shipping">{amount}</span> more for free shipping!',
      unlocked: '✅ <strong>Free shipping unlocked!</strong>',
      startLabel: '0',
      endLabel: '{threshold}'
    },
    de: {
      remaining: 'Dir fehlen noch <span class="amount-to-free-shipping">{amount}</span> für kostenlosen Versand!',
      unlocked: '✅ <strong>Kostenloser Versand freigeschaltet!</strong>',
      startLabel: '0',
      endLabel: '{threshold}'
    },
    fr: {
      remaining: 'Il vous manque encore <span class="amount-to-free-shipping">{amount}</span> pour la livraison gratuite!',
      unlocked: '✅ <strong>Livraison gratuite débloquée!</strong>',
      startLabel: '0',
      endLabel: '{threshold}'
    },
    es: {
      remaining: 'Te faltan <span class="amount-to-free-shipping">{amount}</span> para el envío gratuito!',
      unlocked: '✅ <strong>¡Envío gratuito desbloqueado!</strong>',
      startLabel: '0',
      endLabel: '{threshold}'
    }
  };

  // === STEP 3: CURRENCY CONFIGURATION ===
  const currencyConfig = {
    EUR: { 
      threshold: 79, 
      symbol: '€', 
      format: '{amount}€',
      show: true
    },
    GBP: { 
      threshold: 68, 
      symbol: '£', 
      format: '£{amount}',
      show: true
    },
    USD: { 
      threshold: null, 
      symbol: '$', 
      format: '${amount}',
      show: false // Hide for USD as offer is Europe only
    }
  };

  // === STEP 4: CURRENCY FORMATTING ===
  function formatCurrency(amount, currency) {
    const config = currencyConfig[currency];
    if (!config) return amount.toString();

    let formatted;
    if (currency === 'EUR') {
      // European format: 1.234,56
      formatted = amount.toFixed(2).replace('.', ',');
    } else {
      // US/UK format: 1,234.56
      formatted = amount.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    }

    return config.format.replace('{amount}', formatted);
  }

  // === STEP 5: IMPROVED PARSING FOR DIFFERENT CURRENCIES ===
  function parseAmount(text, currency) {
    if (!text) return 0;

    // Remove common currency symbols and extra whitespace
    let cleaned = text.replace(/[€£$\s]/g, '');

    if (currency === 'EUR') {
      // European format: 1.234,56 or 79,00
      const match = cleaned.match(/(\d{1,3}(?:\.\d{3})*(?:,\d{2})?|\d+(?:,\d{2})?)/);
      if (!match) return 0;
      const normalized = match[1].replace(/\./g, '').replace(',', '.');
      const num = parseFloat(normalized);
      return Number.isFinite(num) ? num : 0;
    } else {
      // US/UK format: 1,234.56 or 79.00
      const match = cleaned.match(/(\d{1,3}(?:,\d{3})*(?:\.\d{2})?|\d+(?:\.\d{2})?)/);
      if (!match) return 0;
      const normalized = match[1].replace(/,/g, '');
      const num = parseFloat(normalized);
      return Number.isFinite(num) ? num : 0;
    }
  }

  function extractTotalFromElement(element) {
    if (!element) return 0;

    const valueAttr = element.getAttribute && element.getAttribute('value');
    if (valueAttr) {
      const v1 = parseAmount(valueAttr, currentCurrency);
      if (v1 > 0) return v1;
    }

    const text = element.textContent || element.innerText || '';
    const v2 = parseAmount(text, currentCurrency);
    if (v2 > 0) return v2;

    return 0;
  }

  // === STEP 6: SAFE UPDATE WITH LOOP PREVENTION ===
  const debouncedUpdateShippingProgress = debounce((cartTotal) => {
    // Less restrictive update check
    if (isUpdating) {
      log('Update already in progress, queuing...');
      // Queue the update instead of skipping
      setTimeout(() => debouncedUpdateShippingProgress(cartTotal), 50);
      return;
    }

    isUpdating = true;
    
    try {
      updateShippingProgressInternal(cartTotal);
    } catch (error) {
      log('Error in updateShippingProgress:', error);
    } finally {
      // Shorter reset time for better responsiveness
      setTimeout(() => {
        isUpdating = false;
      }, 50);
    }
  }, 100); // Reduced from 150ms to 100ms

  function updateShippingProgressInternal(cartTotal) {
    const progressContainer = document.getElementById('dolfi-shipping-progress');
    const progressFill = document.getElementById('progress-fill');
    const shippingMessage = document.getElementById('shipping-message');
    const fireworksContainer = document.getElementById('fireworks-container');
    const startLabel = document.getElementById('start-label');
    const endLabel = document.getElementById('end-label');

    log('UpdateShippingProgressInternal called with:', cartTotal, 'currency:', currentCurrency);

    // For EUR (default) always show - be less restrictive
    const shouldShow = currentCurrency === 'EUR' || currentCurrency === 'GBP' || 
                      (currencyConfig[currentCurrency] && currencyConfig[currentCurrency].show !== false);

    if (!shouldShow && currentCurrency === 'USD') {
      log('Hiding progress for USD');
      if (progressContainer) progressContainer.style.display = 'none';
      return;
    }

    // Show component for EUR/GBP and force show for debugging
    if (progressContainer) {
      progressContainer.style.display = 'block';
      log('Progress container shown');
    }

    if (!progressFill) return;
    if (cartTotal === lastCartTotal) return;

    lastCartTotal = cartTotal;
    log('Updating shipping progress:', cartTotal, currentCurrency);

    const progress = Math.min((cartTotal / currentThreshold) * 100, 100);
    const remaining = Math.max(currentThreshold - cartTotal, 0);

    // Use requestAnimationFrame for smooth UI updates
    requestAnimationFrame(() => {
      progressFill.style.width = progress + '%';

      // Update labels
      if (startLabel) {
        startLabel.textContent = formatCurrency(0, currentCurrency);
      }
      if (endLabel) {
        endLabel.textContent = formatCurrency(currentThreshold, currentCurrency);
      }

      const t = translations[currentLanguage];

      if (remaining > 0) {
        progressFill.classList.remove('complete');
        shippingMessage && shippingMessage.classList.remove('completed');
        fireworksContainer && (fireworksContainer.style.display = 'none');

        if (shippingMessage && t) {
          const formattedRemaining = formatCurrency(remaining, currentCurrency);
          shippingMessage.innerHTML = t.remaining.replace('{amount}', formattedRemaining);
        }
      } else {
        progressFill.classList.add('complete');
        shippingMessage && shippingMessage.classList.add('completed');
        
        if (shippingMessage && t) {
          shippingMessage.innerHTML = t.unlocked;
        }
        
        if (fireworksContainer) {
          fireworksContainer.style.display = 'block';
          setTimeout(() => { fireworksContainer.style.display = 'none'; }, 2000);
        }
      }
    });
  }

  // === STEP 7: SAFE OBSERVER WITH LOOP PROTECTION ===
  function attachValueObserver(cartTotalElement) {
    // Always disconnect existing observer first
    disconnectObservers();
    
    log('Attaching value observer to:', cartTotalElement);

    // Reset counters
    observerTriggerCount = 0;
    isUpdating = false;

    // Initial update
    const initialValue = extractTotalFromElement(cartTotalElement);
    debouncedUpdateShippingProgress(initialValue);

    // Create new observer with loop protection
    valueObserver = new MutationObserver((mutations) => {
      observerTriggerCount++;
      
      // More lenient circuit breaker - was too aggressive at 50
      if (observerTriggerCount > 200) {
        log('Observer loop detected - disconnecting to prevent infinite loops');
        disconnectObservers();
        return;
      }

      // Skip if already updating to prevent cascading updates
      if (isUpdating) {
        log('Skipping observer callback - update in progress');
        return;
      }

      let shouldUpdate = false;

      // Check if relevant mutations occurred
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && 
            (mutation.attributeName === 'value' || mutation.attributeName === 'shimmer')) {
          shouldUpdate = true;
        } else if (mutation.type === 'childList' || mutation.type === 'characterData') {
          shouldUpdate = true;
        }
      });

      if (shouldUpdate) {
        log('Observer triggered update #', observerTriggerCount);
        const newValue = extractTotalFromElement(cartTotalElement);
        debouncedUpdateShippingProgress(newValue);
      }
    });

    // Observe with minimal scope to reduce noise
    valueObserver.observe(cartTotalElement, {
      attributes: true,
      attributeFilter: ['value', 'shimmer'], // Only specific attributes
      childList: true,
      subtree: false, // Don't observe subtree to reduce mutations
      characterData: true
    });

    log('Value observer attached successfully');
  }

  function waitForCartTotalOnce() {
    // Immediate attempt
    const existing = document.querySelector(TARGET_SELECTOR);
    if (existing) {
      log('Cart total element found immediately');
      attachValueObserver(existing);
      return;
    }

    log('Cart total element not found, setting up bootstrap observer');

    // Disconnect any existing bootstrap observer
    if (bootstrapObserver) {
      bootstrapObserver.disconnect();
      bootstrapObserver = null;
    }

    // Bootstrap observer that auto-disconnects when target appears
    let attempts = 0;
    const maxAttempts = 100; // Prevent infinite observation

    bootstrapObserver = new MutationObserver(() => {
      attempts++;
      
      if (attempts > maxAttempts) {
        log('Bootstrap observer max attempts reached, disconnecting');
        bootstrapObserver.disconnect();
        bootstrapObserver = null;
        return;
      }

      const el = document.querySelector(TARGET_SELECTOR);
      if (el) {
        log('Cart total element found by bootstrap observer after', attempts, 'attempts');
        bootstrapObserver.disconnect();
        bootstrapObserver = null;
        attachValueObserver(el);
      }
    });

    bootstrapObserver.observe(document.documentElement, { 
      childList: true, 
      subtree: true 
    });
  }

  // === INITIALIZATION WITH PROPER CLEANUP ===
  function initialize() {
    log('Initializing shipping progress...');

    // If already initialized, perform cleanup first
    if (isInitialized) {
      log('Re-initializing - performing cleanup first');
      cleanup();
    }

    try {
      const detected = detectLanguageAndCurrency();
      currentLanguage = detected.language;
      currentCurrency = detected.currency;
      
      const config = currencyConfig[currentCurrency];
      if (config && config.threshold) {
        currentThreshold = config.threshold;
      }

      log('Initialized with:', { currentLanguage, currentCurrency, currentThreshold });

      // Start the cart total detection process
      waitForCartTotalOnce();
      
      isInitialized = true;
      log('Initialization complete');

    } catch (error) {
      log('Error during initialization:', error);
    }
  }

  // === EVENT HANDLERS WITH SAFE REGISTRATION ===
  function setupEventHandlers() {
    // Section reload handler
    const sectionLoadHandler = (e) => {
      if (e.target && e.target.closest && e.target.closest('.cart-drawer__dialog')) {
        log('Cart drawer section reloaded');
        setTimeout(() => initialize(), 100);
      }
    };

    const sectionUnloadHandler = () => {
      log('Cart drawer section unloaded');
      cleanup();
    };

    const currencyChangeHandler = (e) => {
      log('Currency changed event detected');
      setTimeout(() => initialize(), 100);
    };

    // Register event listeners safely
    addEventListenerSafe(document, 'shopify:section:load', sectionLoadHandler);
    addEventListenerSafe(document, 'shopify:section:unload', sectionUnloadHandler);
    addEventListenerSafe(document, 'currency:changed', currencyChangeHandler);

    // Handle Shopify currency changes with safe override
    if (window.Shopify && window.Shopify.onError && !window.Shopify._originalOnError) {
      window.Shopify._originalOnError = window.Shopify.onError;
      window.Shopify.onError = function(xhr, textStatus) {
        if (textStatus === 'parsererror') {
          // Currency might have changed, re-initialize
          log('Shopify parser error detected, reinitializing...');
          setTimeout(() => initialize(), 100);
        }
        // Call original handler
        if (window.Shopify._originalOnError) {
          return window.Shopify._originalOnError.apply(this, arguments);
        }
      };
    }
  }

  // === PAGE VISIBILITY HANDLER ===
  function setupVisibilityHandler() {
    const visibilityHandler = () => {
      if (document.visibilityState === 'visible') {
        // Reset counters when page becomes visible
        observerTriggerCount = 0;
        isUpdating = false;
      }
    };

    addEventListenerSafe(document, 'visibilitychange', visibilityHandler);
  }

  // === CLEANUP ON PAGE UNLOAD ===
  function setupUnloadHandler() {
    const unloadHandler = () => {
      log('Page unloading, performing cleanup...');
      cleanup();
    };

    addEventListenerSafe(window, 'beforeunload', unloadHandler);
    addEventListenerSafe(window, 'unload', unloadHandler);
  }

  // === MAIN STARTUP ===
  log('Cart drawer shipping progress script loaded');

  // Start initialization
  initialize();
  
  // Setup all event handlers
  setupEventHandlers();
  setupVisibilityHandler();
  setupUnloadHandler();

  // Expose cleanup function for debugging
  if (DEBUG) {
    window.shippingProgressCleanup = cleanup;
    window.shippingProgressInit = initialize;
  }
});